import { CaretDown } from "@phosphor-icons/react";
import { forwardRef, useId, useState, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type AccordionItem = {
  value: string;
  title: ReactNode;
  description?: ReactNode;
  content: ReactNode;
  disabled?: boolean;
};

export type AccordionType = "single" | "multiple";

export type AccordionProps = Omit<HTMLAttributes<HTMLDivElement>, "defaultValue" | "onChange"> & {
  items: AccordionItem[];
  /** `"single"` keeps one panel open at a time; `"multiple"` allows several. */
  type?: AccordionType;
  /** Controlled open value(s): a string for `single`, a string array for `multiple`. */
  value?: string | string[];
  /** Uncontrolled initial open value(s). */
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  /** When `type="single"`, allow closing the open panel by clicking it again. */
  collapsible?: boolean;
};

const toArray = (value: string | string[] | undefined): string[] => {
  if (value === undefined || value === "") return [];
  return Array.isArray(value) ? value : [value];
};

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  ({ className, items, type = "single", value, defaultValue, onValueChange, collapsible = true, ...props }, ref) => {
    const baseId = useId();
    const isControlled = value !== undefined;
    const [internalOpen, setInternalOpen] = useState<string[]>(() => toArray(defaultValue));
    const openValues = isControlled ? toArray(value) : internalOpen;

    const commit = (next: string[]) => {
      if (!isControlled) setInternalOpen(next);
      onValueChange?.(type === "single" ? next[0] ?? "" : next);
    };

    const toggle = (itemValue: string) => {
      const isOpen = openValues.includes(itemValue);

      if (type === "single") {
        if (isOpen) {
          commit(collapsible ? [] : openValues);
        } else {
          commit([itemValue]);
        }
        return;
      }

      commit(isOpen ? openValues.filter((current) => current !== itemValue) : [...openValues, itemValue]);
    };

    return (
      <div ref={ref} className={cn("bfrs-overflow-hidden bfrs-rounded-xl bfrs-border bfrs-border-border bfrs-bg-surface-card", className)} {...props}>
        {items.map((item, index) => {
          const isOpen = openValues.includes(item.value);
          const triggerId = `${baseId}-trigger-${index}`;
          const panelId = `${baseId}-panel-${index}`;

          return (
            <div key={item.value} className={cn(index > 0 && "bfrs-border-t bfrs-border-border")}>
              <button
                type="button"
                id={triggerId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                disabled={item.disabled}
                onClick={() => toggle(item.value)}
                className="bfrs-flex bfrs-w-full bfrs-items-center bfrs-gap-3 bfrs-px-4 bfrs-py-3 bfrs-text-left bfrs-outline-none focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-60"
              >
                <span className="bfrs-min-w-0 bfrs-flex-1">
                  <span className="bfrs-block bfrs-text-sm bfrs-font-semibold bfrs-text-text-primary">{item.title}</span>
                  {item.description && <span className="bfrs-mt-0.5 bfrs-block bfrs-text-xs bfrs-leading-5 bfrs-text-text-secondary">{item.description}</span>}
                </span>
                <CaretDown size={16} weight="bold" className={cn("bfrs-shrink-0 bfrs-text-text-muted bfrs-transition-transform", isOpen && "bfrs-rotate-180")} />
              </button>
              {isOpen && (
                <div id={panelId} role="region" aria-labelledby={triggerId} className="bfrs-border-t bfrs-border-border bfrs-px-4 bfrs-py-3">
                  {item.content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);

Accordion.displayName = "Accordion";
